import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Card, CardBody, CardHeader, CardTitle } from "@progress/kendo-react-layout";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import type { SupportedSoftware } from "@domain/repositories";
import type { SoftwareEnumSchema } from "@domain/schemas/BI";
import { getSoftwareAccent, type SoftwareAccent } from "@domain/software";
import { extractProblemDetails } from "@presentation/common";
import { useInjection } from "@presentation/components/dependencyInjection";
import { licensedSoftwareOrNull, useLicensedSoftware } from "@presentation/components/job/components/useLicensedSoftware";

const DEFAULT_BADGE_STYLE: React.CSSProperties = {
  marginLeft: "8px",
  padding: "2px 6px",
  borderRadius: "999px",
  backgroundColor: "#e0f2fe",
  color: "#075985",
  fontSize: "12px",
};

const SECTION_CARD_STYLE: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "14px",
  backgroundColor: "#f8fafc",
};

const MINOR_SECTION_STYLE: React.CSSProperties = {
  borderLeft: "2px solid #e2e8f0",
  paddingLeft: "12px",
};

const SUPPORTED_SOFTWARE_LIST: SupportedSoftware[] = ["Spectronaut", "SpectroMine"];

/**
Stable empty set so the memoised render does not see a new object each pass.
*/
const EMPTY_COLLAPSED: ReadonlySet<number> = new Set<number>();

// The Spectronaut key is kept verbatim so previously stored admin preferences survive this change.
const defaultTrackingStorageKey = (software: SupportedSoftware): string => (software === "SpectroMine" ? "spectromine-default-tracking" : "spectronaut-default-tracking");

type DefaultTrackingMode = { kind: "latest" } | { kind: "major"; majorVersion: number } | { kind: "minor"; majorVersion: number; minorVersion: number } | { kind: "manual" };

type VersionGroup<T> = {
  key: string;
  majorVersion: number;
  minorVersion?: number;
  versions: T[];
};

const parseVersion = (version: string) => {
  const match = /^(?:[A-Z]{2})?(\d+)\.(\d+)\.(\d+)/.exec(version);
  if (!match) {
    return null;
  }

  return {
    majorVersion: Number(match[1]),
    minorVersion: Number(match[2]),
    patchVersion: Number(match[3]),
  };
};

const getLatestVersionByMajor = (versions: string[], majorVersion: number): string | null => {
  const parsedVersions = versions
    .map((version) => ({ version, parsed: parseVersion(version) }))
    .filter((item): item is { version: string; parsed: NonNullable<ReturnType<typeof parseVersion>> } => item.parsed !== null && item.parsed.majorVersion === majorVersion)
    .toSorted((a, b) => {
      if (b.parsed.minorVersion !== a.parsed.minorVersion) {
        return b.parsed.minorVersion - a.parsed.minorVersion;
      }

      return b.parsed.patchVersion === a.parsed.patchVersion ? b.version.localeCompare(a.version, undefined, { numeric: true }) : b.parsed.patchVersion - a.parsed.patchVersion;
    });

  return parsedVersions[0]?.version ?? null;
};

const getLatestVersionByMinor = (versions: string[], majorVersion: number, minorVersion: number): string | null => {
  const parsedVersions = versions
    .map((version) => ({ version, parsed: parseVersion(version) }))
    .filter(
      (item): item is { version: string; parsed: NonNullable<ReturnType<typeof parseVersion>> } =>
        item.parsed !== null && item.parsed.majorVersion === majorVersion && item.parsed.minorVersion === minorVersion,
    )
    .toSorted((a, b) => {
      return b.parsed.patchVersion === a.parsed.patchVersion ? b.version.localeCompare(a.version, undefined, { numeric: true }) : b.parsed.patchVersion - a.parsed.patchVersion;
    });

  return parsedVersions[0]?.version ?? null;
};

const getLatestVersion = (versions: string[]): string | null => {
  const parsedVersions = versions
    .map((version) => ({ version, parsed: parseVersion(version) }))
    .filter((item): item is { version: string; parsed: NonNullable<ReturnType<typeof parseVersion>> } => item.parsed !== null)
    .toSorted((a, b) => {
      if (b.parsed.majorVersion !== a.parsed.majorVersion) {
        return b.parsed.majorVersion - a.parsed.majorVersion;
      }

      if (b.parsed.minorVersion !== a.parsed.minorVersion) {
        return b.parsed.minorVersion - a.parsed.minorVersion;
      }

      return b.parsed.patchVersion === a.parsed.patchVersion ? b.version.localeCompare(a.version, undefined, { numeric: true }) : b.parsed.patchVersion - a.parsed.patchVersion;
    });

  return parsedVersions[0]?.version ?? null;
};

const sortGroupKeys = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true });

const groupCandidateVersions = (versions: string[]): VersionGroup<string>[] => {
  const groups = new Map<string, VersionGroup<string>>();

  for (const version of versions) {
    const parsed = parseVersion(version);
    const key = parsed ? `${parsed.majorVersion}.${parsed.minorVersion}` : "Other";
    const group = groups.get(key) ?? {
      key,
      majorVersion: parsed?.majorVersion ?? 0,
      minorVersion: parsed?.minorVersion,
      versions: [],
    };
    group.versions.push(version);
    groups.set(key, group);
  }

  return [...groups.values()]
    .toSorted((a, b) => sortGroupKeys(a.key, b.key))
    .map((group) => ({
      ...group,
      versions: group.versions.toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true })),
    }));
};

const formatError = (error: unknown, fallback: string) => {
  const { title, detail, messages } = extractProblemDetails(error);
  return [title, detail, ...messages].filter((message): message is string => !!message?.trim()).join(" ") || fallback;
};

// Built per engine so the whole page reads in the active engine's colour.
const makeVersionButtonStyle =
  (accent: SoftwareAccent) =>
  (isSelected: boolean, isCompact = false): React.CSSProperties => ({
    backgroundColor: isSelected ? accent.background : "#ffffff",
    borderColor: isSelected ? accent.border : "#cbd5e1",
    color: isSelected ? accent.text : "#0f172a",
    padding: isCompact ? "4px 10px" : "6px 12px",
  });

// Each tab carries its own engine's colour, so the two are distinguishable even
// before one is selected.
const engineTabStyle = (accent: SoftwareAccent, isSelected: boolean): React.CSSProperties => ({
  backgroundColor: isSelected ? accent.background : "#ffffff",
  borderColor: accent.border,
  color: isSelected ? accent.text : "#475569",
  fontWeight: isSelected ? 600 : 400,
  padding: "6px 14px",
});

const readDefaultTrackingMode = (software: SupportedSoftware): DefaultTrackingMode | null => {
  try {
    const rawValue = localStorage.getItem(defaultTrackingStorageKey(software));
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue) as Partial<DefaultTrackingMode>;
    if (parsedValue.kind === "latest" || parsedValue.kind === "manual") {
      return parsedValue as DefaultTrackingMode;
    }

    if (parsedValue.kind === "major" && typeof parsedValue.majorVersion === "number") {
      return { kind: "major", majorVersion: parsedValue.majorVersion };
    }

    if (parsedValue.kind === "minor" && typeof parsedValue.majorVersion === "number" && typeof parsedValue.minorVersion === "number") {
      return {
        kind: "minor",
        majorVersion: parsedValue.majorVersion,
        minorVersion: parsedValue.minorVersion,
      };
    }

    return null;
  } catch {
    return null;
  }
};

const writeDefaultTrackingMode = (software: SupportedSoftware, mode: DefaultTrackingMode | null) => {
  if (!mode) {
    localStorage.removeItem(defaultTrackingStorageKey(software));
    return;
  }

  localStorage.setItem(defaultTrackingStorageKey(software), JSON.stringify(mode));
};

const resolveTrackedDefaultVersion = (mode: DefaultTrackingMode | null, versions: string[]): string | null => {
  if (!mode || mode.kind === "manual") {
    return null;
  }

  if (mode.kind === "latest") {
    return getLatestVersion(versions);
  }

  return mode.kind === "major" ? getLatestVersionByMajor(versions, mode.majorVersion) : getLatestVersionByMinor(versions, mode.majorVersion, mode.minorVersion);
};

const hasDynamicDefault = (mode: DefaultTrackingMode | null): boolean => !!mode && mode.kind !== "manual";

const isVersionInTrackedMajor = (version: string, majorVersion: number): boolean => {
  const parsed = parseVersion(version);
  return parsed?.majorVersion === majorVersion;
};

const isVersionInTrackedMinor = (version: string, majorVersion: number, minorVersion: number): boolean => {
  const parsed = parseVersion(version);
  return parsed?.majorVersion === majorVersion && parsed?.minorVersion === minorVersion;
};

const SoftwareVersionsPage: React.FC = () => {
  const injectedVersionsService = useInjection(ListSoftwareVersionsService);
  const versionsService = useRef(injectedVersionsService).current;
  const licensedSoftware = licensedSoftwareOrNull(useLicensedSoftware());
  const [software, setSoftware] = useState<SupportedSoftware>("Spectronaut");
  const [candidateVersions, setCandidateVersions] = useState<string[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<Set<string>>(new Set());
  const [defaultVersion, setDefaultVersion] = useState<string | null>(null);
  const [defaultTrackingMode, setDefaultTrackingMode] = useState<DefaultTrackingMode | null>(() => readDefaultTrackingMode("Spectronaut"));
  const defaultTrackingModeReference = useRef<DefaultTrackingMode | null>(defaultTrackingMode);
  const [error, setError] = useState("");
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [globalVersionFilter, setGlobalVersionFilter] = useState("");
  // Kept per engine so switching back to an engine restores what the user had closed.
  // Every engine starts fully expanded, so only explicitly collapsed majors are tracked.
  const [collapsedMajorVersionsBySoftware, setCollapsedMajorVersionsBySoftware] = useState<Partial<Record<SupportedSoftware, Set<number>>>>({});
  const collapsedMajorVersions = collapsedMajorVersionsBySoftware[software] ?? EMPTY_COLLAPSED;

  const accent = useMemo(() => getSoftwareAccent(software as unknown as SoftwareEnumSchema), [software]);
  const versionButtonStyle = useMemo(() => makeVersionButtonStyle(accent), [accent]);

  // Only offer engines that actually have a license in the cluster. While the status is still unknown we
  // show every supported engine instead of falling back to Spectronaut, otherwise the first render hides
  // the engine tabs and the page looks like the single-engine version of the UI.
  const availableSoftware = useMemo<SupportedSoftware[]>(() => {
    if (licensedSoftware === null) {
      return SUPPORTED_SOFTWARE_LIST;
    }

    const licensed = SUPPORTED_SOFTWARE_LIST.filter((candidate) => licensedSoftware.includes(candidate));
    return licensed.length > 0 ? licensed : ["Spectronaut"];
  }, [licensedSoftware]);

  useEffect(() => {
    if (!availableSoftware.includes(software)) {
      setSoftware(availableSoftware[0]);
    }
  }, [availableSoftware, software]);

  useEffect(() => {
    defaultTrackingModeReference.current = defaultTrackingMode;
  }, [defaultTrackingMode]);

  const updateDefaultTrackingMode = useCallback(
    (mode: DefaultTrackingMode | null) => {
      setDefaultTrackingMode(mode);
      writeDefaultTrackingMode(software, mode);
    },
    [software],
  );

  const persistSelectedVersions = useCallback(
    (next: Set<string>) => {
      void versionsService
        .setSelectedSpectronautVersionsAsync(candidateVersions, [...next], software)
        .catch((saveError) => setError(formatError(saveError, `Failed to save selected ${software} versions.`)));
    },
    [candidateVersions, software, versionsService],
  );

  const persistDefaultVersion = useCallback(
    (version: string) => {
      setDefaultVersion(version);
      void versionsService.setDefaultSpectronautVersionAsync(version, software).catch((saveError) => setError(formatError(saveError, `Failed to save the default ${software} version.`)));
    },
    [software, versionsService],
  );

  const loadVersions = useCallback(async () => {
    setCandidatesLoading(true);
    setError("");
    setCandidateVersions([]);
    setSelectedVersions(new Set());
    setDefaultVersion(null);
    setGlobalVersionFilter("");

    // Tracking preferences are stored per engine, so re-read them whenever the selected engine changes.
    const storedTrackingMode = readDefaultTrackingMode(software);
    defaultTrackingModeReference.current = storedTrackingMode;
    setDefaultTrackingMode(storedTrackingMode);

    try {
      const candidates = await versionsService.getCandidateVersionsListAsync(software);
      setCandidateVersions(candidates);
      const selected = await versionsService.getSelectedSpectronautVersionsAsync(candidates, software);
      const nextDefaultVersion = await versionsService.getDefaultSpectronautVersionAsync(candidates, software);
      setSelectedVersions(new Set(selected));
      setDefaultVersion(nextDefaultVersion ?? null);

      const trackedVersion = resolveTrackedDefaultVersion(defaultTrackingModeReference.current, candidates);
      if (trackedVersion) {
        const nextSelected = new Set(selected);
        nextSelected.add(trackedVersion);
        setSelectedVersions(nextSelected);
        void versionsService
          .setSelectedSpectronautVersionsAsync(candidates, [...nextSelected], software)
          .catch((saveError) => setError(formatError(saveError, `Failed to save selected ${software} versions.`)));
        setDefaultVersion(trackedVersion);
        void versionsService.setDefaultSpectronautVersionAsync(trackedVersion, software).catch((saveError) => setError(formatError(saveError, `Failed to save the default ${software} version.`)));
      }
    } catch (loadError) {
      setError((currentError) => [currentError, formatError(loadError, "Failed to load candidate software versions.")].filter(Boolean).join(" "));
    } finally {
      setCandidatesLoading(false);
    }
  }, [software, versionsService]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const candidateVersionGroups = useMemo(() => groupCandidateVersions(candidateVersions), [candidateVersions]);
  const majorGroups = useMemo(() => {
    const groups = new Map<number, VersionGroup<string>[]>();
    const groupableVersions = candidateVersionGroups.filter((group) => group.key !== "Other");
    for (const group of groupableVersions) {
      const majorGroups = groups.get(group.majorVersion) ?? [];
      majorGroups.push(group);
      groups.set(group.majorVersion, majorGroups);
    }

    return [...groups].toSorted(([majorA], [majorB]) => majorB - majorA);
  }, [candidateVersionGroups]);

  const toggleVersions = (versions: string[]) => {
    setSelectedVersions((current) => {
      const next = new Set(current);
      const allSelected = versions.every((version) => next.has(version));
      for (const version of versions) {
        if (allSelected) {
          next.delete(version);
        } else {
          next.add(version);
        }
      }
      persistSelectedVersions(next);
      if (!next.has(defaultVersion ?? "")) {
        const fallbackDefault = candidateVersions.find((candidate) => next.has(candidate)) ?? null;
        if (fallbackDefault) {
          persistDefaultVersion(fallbackDefault);
        } else {
          setDefaultVersion(null);
        }
      }

      if (allSelected && defaultTrackingMode?.kind === "major") {
        const isTrackedMajorGroup = versions.every((version) => isVersionInTrackedMajor(version, defaultTrackingMode.majorVersion));
        if (isTrackedMajorGroup) {
          updateDefaultTrackingMode({ kind: "manual" });
        }
      }

      if (allSelected && defaultTrackingMode?.kind === "minor") {
        const isTrackedMinorGroup = versions.every((version) => isVersionInTrackedMinor(version, defaultTrackingMode.majorVersion, defaultTrackingMode.minorVersion));
        if (isTrackedMinorGroup) {
          updateDefaultTrackingMode({ kind: "manual" });
        }
      }
      return next;
    });
  };

  const toggleVersion = (version: string) => {
    setSelectedVersions((current) => {
      const next = new Set(current);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      persistSelectedVersions(next);
      if (!next.has(defaultVersion ?? "")) {
        setDefaultVersion(null);
      }
      return next;
    });
  };

  const toggleMajorExpansion = (majorVersion: number) => {
    setCollapsedMajorVersionsBySoftware((current) => {
      const next = new Set(current[software]);
      if (next.has(majorVersion)) {
        next.delete(majorVersion);
      } else {
        next.add(majorVersion);
      }
      return { ...current, [software]: next };
    });
  };

  const setTrackedDefault = (mode: DefaultTrackingMode, event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const nextDefaultVersion = resolveTrackedDefaultVersion(mode, candidateVersions);
    if (!nextDefaultVersion) {
      return;
    }

    updateDefaultTrackingMode(mode);
    setSelectedVersions((current) => {
      const next = new Set(current);
      next.add(nextDefaultVersion);
      persistSelectedVersions(next);
      return next;
    });
    persistDefaultVersion(nextDefaultVersion);
  };

  const setTrackedMinorDefault = (event: React.MouseEvent<HTMLElement>, majorVersion: number, group: VersionGroup<string>) => {
    event.preventDefault();
    event.stopPropagation();

    const minorVersion = group.minorVersion;
    const nextDefaultVersion = group.versions[0] ?? null;
    if (minorVersion === undefined || !nextDefaultVersion) {
      return;
    }

    const mode: DefaultTrackingMode = { kind: "minor", majorVersion, minorVersion };
    updateDefaultTrackingMode(mode);
    setSelectedVersions((current) => {
      const next = new Set(current);
      next.add(nextDefaultVersion);
      persistSelectedVersions(next);
      return next;
    });
    persistDefaultVersion(nextDefaultVersion);
  };

  const setDefaultVersionFromContextMenu = (event: React.MouseEvent<HTMLElement>, version: string) => {
    event.preventDefault();
    updateDefaultTrackingMode({ kind: "manual" });

    setSelectedVersions((current) => {
      if (current.has(version)) {
        return current;
      }

      const next = new Set(current);
      next.add(version);
      persistSelectedVersions(next);
      return next;
    });
    persistDefaultVersion(version);
  };

  const allSelected = candidateVersions.length > 0 && candidateVersions.every((version) => selectedVersions.has(version));
  const globalFilterTerm = globalVersionFilter.trim().toLowerCase();
  const hasGlobalFilter = globalFilterTerm.length > 0;

  useEffect(() => {
    const trackedVersion = resolveTrackedDefaultVersion(defaultTrackingMode, candidateVersions);
    if (!trackedVersion || trackedVersion === defaultVersion) {
      return;
    }

    setSelectedVersions((current) => {
      if (current.has(trackedVersion)) {
        return current;
      }

      const next = new Set(current);
      next.add(trackedVersion);
      persistSelectedVersions(next);
      return next;
    });
    persistDefaultVersion(trackedVersion);
  }, [candidateVersions, defaultTrackingMode, defaultVersion, persistDefaultVersion, persistSelectedVersions]);

  return (
    <div style={{ padding: "16px" }}>
      <Card>
        <CardHeader>
          <CardTitle>{software} Versions</CardTitle>
        </CardHeader>
        <CardBody>
          {availableSoftware.length > 1 && (
            <div role="tablist" aria-label="Search engine" style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              {availableSoftware.map((candidate) => (
                <Button
                  key={candidate}
                  role="tab"
                  aria-selected={software === candidate}
                  fillMode={software === candidate ? "solid" : "outline"}
                  style={engineTabStyle(getSoftwareAccent(candidate as unknown as SoftwareEnumSchema), software === candidate)}
                  onClick={() => setSoftware(candidate)}
                >
                  {candidate}
                </Button>
              ))}
            </div>
          )}

          <p style={{ marginTop: 0 }}>
            Versions found in the registry are available for job setup automatically. Deselect a version here to hide it from job setup. Right click on a version to make it default
          </p>

          {error && (
            <div role="alert" style={{ marginBottom: "12px", color: "#b00020" }}>
              {error}
            </div>
          )}

          {candidatesLoading && <p>Loading registry tags...</p>}
          {!candidatesLoading &&
            (candidateVersions.length === 0 ? (
              <p>No registry tags are available.</p>
            ) : (
              <div style={{ display: "grid", gap: "16px" }}>
                <div>
                  <Button
                    fillMode={allSelected ? "solid" : "outline"}
                    style={versionButtonStyle(allSelected)}
                    onClick={() => toggleVersions(candidateVersions)}
                    onContextMenu={(event) => setTrackedDefault({ kind: "latest" }, event)}
                    title="Right-click to track latest as default"
                  >
                    All {software} versions
                    {defaultTrackingMode?.kind === "latest" && <span style={DEFAULT_BADGE_STYLE}>Latest version is default</span>}
                  </Button>
                </div>

                <div style={{ maxWidth: "440px" }}>
                  <input
                    type="text"
                    value={globalVersionFilter}
                    placeholder="Search all versions ..."
                    aria-label="Search all versions"
                    onChange={(event) => setGlobalVersionFilter(event.target.value)}
                    style={{
                      width: "100%",
                      border: "1px solid #cbd5e1",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                {majorGroups.map(([majorVersion, minorGroups]) => {
                  const majorVersions = minorGroups.flatMap((group) => group.versions);
                  const majorSelected = majorVersions.every((version) => selectedVersions.has(version));
                  const visibleMinorGroups = hasGlobalFilter
                    ? minorGroups
                        .map((group) => ({
                          ...group,
                          versions: group.versions.filter((candidate) => candidate.toLowerCase().includes(globalFilterTerm) || group.key.toLowerCase().includes(globalFilterTerm)),
                        }))
                        .filter((group) => group.versions.length > 0)
                    : minorGroups;

                  if (visibleMinorGroups.length === 0) {
                    return null;
                  }

                  const isExpanded = hasGlobalFilter || !collapsedMajorVersions.has(majorVersion);

                  return (
                    <section key={majorVersion} style={SECTION_CARD_STYLE}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginBottom: isExpanded ? "12px" : 0 }}>
                        <Button
                          fillMode={majorSelected ? "solid" : "outline"}
                          style={versionButtonStyle(majorSelected)}
                          onClick={() => toggleVersions(majorVersions)}
                          onContextMenu={(event) => setTrackedDefault({ kind: "major", majorVersion }, event)}
                          title="Right-click to track this major as default"
                        >
                          {software} {majorVersion}
                          {defaultTrackingMode?.kind === "major" && defaultTrackingMode.majorVersion === majorVersion && (
                            <span style={DEFAULT_BADGE_STYLE}>{`Latest v${majorVersion} is default`}</span>
                          )}
                        </Button>

                        <Button
                          fillMode="outline"
                          style={versionButtonStyle(false, true)}
                          onClick={() => toggleMajorExpansion(majorVersion)}
                          disabled={hasGlobalFilter}
                          title={isExpanded ? `Collapse ${software} ${majorVersion}` : `Expand ${software} ${majorVersion}`}
                        >
                          {isExpanded ? "-" : "+"}
                        </Button>
                      </div>

                      {!isExpanded && (
                        <p style={{ margin: 0, color: "#475569", fontSize: "13px" }}>
                          {minorGroups.length} minor versions, {majorVersions.length} builds
                        </p>
                      )}

                      {isExpanded && (
                        <div style={{ display: "grid", gap: "12px", paddingLeft: "10px" }}>
                          {visibleMinorGroups.map((group) => {
                            const minorSelected = group.versions.every((version) => selectedVersions.has(version));
                            return (
                              <section key={group.key} style={MINOR_SECTION_STYLE}>
                                <div style={{ marginBottom: "8px" }}>
                                  <Button
                                    fillMode={minorSelected ? "solid" : "outline"}
                                    style={versionButtonStyle(minorSelected)}
                                    onClick={() => toggleVersions(group.versions)}
                                    onContextMenu={(event) => setTrackedMinorDefault(event, majorVersion, group)}
                                    title="Right-click to track this minor as default"
                                  >
                                    {group.key}
                                    {defaultTrackingMode?.kind === "minor" && defaultTrackingMode.majorVersion === majorVersion && defaultTrackingMode.minorVersion === (group.minorVersion ?? 0) && (
                                      <span style={DEFAULT_BADGE_STYLE}>{`Latest v${group.key} is default`}</span>
                                    )}
                                  </Button>
                                </div>
                                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", paddingLeft: "14px" }}>
                                  {group.versions.map((candidate) => (
                                    <Button
                                      key={candidate}
                                      fillMode={selectedVersions.has(candidate) ? "solid" : "outline"}
                                      style={versionButtonStyle(selectedVersions.has(candidate))}
                                      onClick={() => toggleVersion(candidate)}
                                      onContextMenu={(event) => setDefaultVersionFromContextMenu(event, candidate)}
                                      title="Right-click to set as default"
                                    >
                                      {candidate}
                                      {defaultVersion === candidate && !hasDynamicDefault(defaultTrackingMode) && <span style={DEFAULT_BADGE_STYLE}>Fixed default</span>}
                                    </Button>
                                  ))}
                                </div>
                              </section>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}

                {candidateVersionGroups
                  .filter((group) => group.key === "Other")
                  .map((group) => (
                    <section key={group.key} style={{ border: "1px solid #e1e5ea", borderRadius: "6px", padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {group.versions.map((candidate) => (
                          <Button
                            key={candidate}
                            fillMode={selectedVersions.has(candidate) ? "solid" : "outline"}
                            style={versionButtonStyle(selectedVersions.has(candidate))}
                            onClick={() => toggleVersion(candidate)}
                            onContextMenu={(event) => setDefaultVersionFromContextMenu(event, candidate)}
                            title="Right-click to set as default"
                          >
                            {candidate}
                            {defaultVersion === candidate && !hasDynamicDefault(defaultTrackingMode) && <span style={DEFAULT_BADGE_STYLE}>Fixed default</span>}
                          </Button>
                        ))}
                      </div>
                    </section>
                  ))}
              </div>
            ))}
        </CardBody>
      </Card>
    </div>
  );
};

export default SoftwareVersionsPage;
